<div class="h-full w-full flex flex-col overflow-hidden">


    @if (isProcessing()) {
    <app-generation-loader title="Value Engineering"
        description="Transform complex documents into actionable summaries" [animationData]="generationAnimationData"
        [bottomImageSvg]="GEOTECHNICAL_ANALYSIS_SVG.VALUE_ENGINEERING_LOADER">
    </app-generation-loader>
    }


    @else if (isEmpty()) {
    <div class="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
        <app-icon [type]="'table-2'" [source]="'lucide'" [size]="'xl'" />
        <span class="text-sm">No value engineering data available</span>
    </div>
    }


    @else if (isFailed()) {
    <div class="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
        <app-icon [type]="'triangle-alert'" [source]="'lucide'" [size]="'xl'" class="text-danger" />
        <span class="text-sm">{{ data()?.error || 'Value engineering generation failed' }}</span>
        <app-button label="Retry" variant="outlined" severity="contrast" (clicked)="load()" />
    </div>
    }

    @else {
    <div class="flex flex-col h-full p-3 gap-2">

        @if (stabilizationColumns().length) {
        <div class="max-h-[50%] flex flex-col rounded-2xl border">
            <div class="flex justify-between items-center px-4 pt-4 pb-2 ml-2 flex-shrink-0">
                <h3 class="text-md font-semibold text-gray-700">Stabilization Methods</h3>
                <div class="flex items-center gap-1">
                    <app-button [icon]="'maximize-2'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                        severity="contrast" pTooltip="Expand" tooltipPosition="top"
                        (clicked)="expandedTable = 'stabilizationMethods'">
                    </app-button>
                    <app-button [icon]="'pencil'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                        severity="contrast" pTooltip="Edit" tooltipPosition="top"
                        (clicked)="analysisExcelDialogVisible = true">
                    </app-button>
                </div>
            </div>
            <div class="overflow-y-auto px-4 pb-4">
                <app-data-table [data]="stabilizationTableData()" [columns]="stabilizationTableColumns()"
                    [config]="tableConfig" dataKey="_index" [showScrollBar]="true" />
            </div>
        </div>
        }

        @if (alternatives().length) {
        <div class="max-h-[50%] flex flex-col rounded-2xl border">
            <div class="flex justify-between items-center px-4 pt-4 pb-2 flex-shrink-0">
                <h3 class="text-sm font-semibold text-gray-700">Alternative analysis results</h3>
                <app-button [icon]="'maximize-2'" [iconSource]="'lucide'" [size]="'default'" variant="outlined"
                    severity="contrast" pTooltip="Expand" tooltipPosition="top"
                    (clicked)="expandedTable = 'alternativeResults'">
                </app-button>
            </div>
            <div class="overflow-y-auto px-4 pb-4">
                <app-data-table [data]="alternativeTableData()" [columns]="alternativeTableColumns()"
                    [config]="tableConfig" dataKey="parameter" [rowClass]="alternativeRowClass"
                    [customTemplates]="alternativeCustomTemplates" (headerChange)="onAltHeaderChange($event)" />
            </div>
        </div>

        }

    </div>
    }

</div>

<app-dialog [visible]="expandedTable === 'stabilizationMethods'" (closed)="expandedTable = null"
    title="Stabilization Methods" class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        <div class="px-6 py-4 max-h-[75vh] overflow-y-auto scrollbar-thin">
            <app-data-table [data]="stabilizationTableData()" [columns]="stabilizationTableColumns()"
                [config]="tableConfig" dataKey="_index" [showScrollBar]="true" />
        </div>
    </ng-template>
</app-dialog>

<app-dialog [visible]="expandedTable === 'alternativeResults'" (closed)="expandedTable = null"
    title="Alternative analysis results" class="max-w-[90vw] w-[90vw]">
    <ng-template #bodyTemplate>
        <div class="px-6 py-4 max-h-[75vh] overflow-y-auto scrollbar-thin">
            <app-data-table [data]="alternativeTableData()" [columns]="alternativeTableColumns()" [config]="tableConfig"
                dataKey="parameter" [rowClass]="alternativeRowClass" [customTemplates]="alternativeCustomTemplates"
                (headerChange)="onAltHeaderChange($event)" [showScrollBar]="true" />
        </div>
    </ng-template>
</app-dialog>

<ng-template #methodCellTpl let-row>
    @if (row._isLayerHeader) {
    <span class="text-xs font-semibold tracking-wide">Layer: {{ row._layer }}</span>
    } @else {
    <span class="text-gray-600 font-medium">{{ row.parameter }}</span>
    }
</ng-template>

<ng-template #valueCellTpl let-row let-col="column">
    @if (!row._isLayerHeader) {
    @let cellStatus = row[col.field + '__status'];
    @if (cellStatus === VALUE_ENGINEERING_STATUS.NOT_STARTED || cellStatus === VALUE_ENGINEERING_STATUS.PROCESSING) {
    <div class="flex items-center justify-center w-full">
        <app-icon [type]="'loader-circle'" [source]="'lucide'" [size]="'xs'" class="animate-spin text-gray-400" />
    </div>
    } @else if (cellStatus === VALUE_ENGINEERING_STATUS.FAILED) {
    <div class="flex items-center justify-center w-full">
        <app-icon [type]="'triangle-alert'" [source]="'lucide'" [size]="'xs'" class="text-yellow-500" />
    </div>
    } @else {
    <span class="text-gray-700">{{ row[col.field] ?? '—' }}</span>
    }
    }
</ng-template>

<p-dialog header="Analysis Excel" [(visible)]="analysisExcelDialogVisible" [modal]="true" [draggable]="false"
    [resizable]="false" appendTo="body" [style]="{ width: '92vw', maxWidth: '96rem' }"
    contentStyleClass="!p-0 overflow-hidden" styleClass="analysis-excel-preview-dialog"
    (onHide)="activeExcelSheet.set(null)">
    <ng-template pTemplate="content">
        @let analysisExcelPreview = analysisExcelPreviewFile();
        @let analysisExcelEditor = analysisExcelEditorConfig();
        <div class="h-[78vh] bg-white">
            @if (analysisExcelEditor && analysisExcelPreview) {
            <app-only-office-doc-editor class="block h-full w-full" [contractId]="analysisExcelPreview._id"
                [configData]="analysisExcelEditor" [editorId]="'analysis_excel_vendor_' + analysisExcelPreview._id">
            </app-only-office-doc-editor>
            } @else {
            <div class="flex h-full items-center justify-center px-6 text-sm text-gray-500">
                Analysis excel is not available.
            </div>
            }
        </div>
    </ng-template>
</p-dialog>